import { Component, inject, OnDestroy } from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { LoaderService } from '../../../core/services/loader.service';
import { CommonService } from '../../../shared/services/common.service';
import { ManageSettingsService } from '../../manage-settings/manage-settings.service';
import { finalize, Observable, Subject, takeUntil } from 'rxjs';
import {
  ApiResponseReferenceMaterialsPaginated,
  ReferenceMaterials,
} from '../../manage-settings/manage-settings.interface';
import { ErrorResponse } from '../../../core/models/error.models';
import { ManageOrganizationDrawerParams } from '../manage-organization.interface';
import { DRAWER_IDS } from '../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../core/services/drawer.service';
import { OrgManagementMode } from '../manage-organization.enums';
import { CommonModule } from '@angular/common';
import { ConfirmDialogComponent } from '../../../components/confirm-dialog/confirm-dialog.component';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { AddReferenceMaterialComponent } from '../../manage-settings/reference-materials/add-reference-material/add-reference-material.component';
import { SettingMode } from '../../manage-settings/manage-settings.enums';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
  selector: 'app-custom-resources',
  standalone: true,
  imports: [
    NzTableModule,
    SvgIconComponent,
    CommonModule,
    ConfirmDialogComponent,
    NzSpinModule,
    NzButtonModule,
  ],
  templateUrl: './custom-resources.component.html',
})
export class CustomResourcesComponent implements OnDestroy {
  private readonly manageSettingSerivce = inject(ManageSettingsService);
  pageSize = 10;
  pageIndex = 1;
  total = 0;
  sort = '';
  sortOrder: number | undefined = 0;
  referenceMaterials$: Observable<ApiResponseReferenceMaterialsPaginated | null> =
    this.manageSettingSerivce.referenceMaterials$;
  private readonly destroy$ = new Subject<void>();
  loading$: Observable<boolean>;
  orgParams: ManageOrganizationDrawerParams = {
    organizationId: 0,
    mode: OrgManagementMode.VIEW,
  };
  isDeleting = false;
  listOfColumn = [
    {
      title: 'Name',
      compare: null,
      priority: false,
    },
    {
      title: 'Description',
      compare: null,
    },
    {
      title: '',
      compare: null,
    },
  ];
  showConfirmDialog = false;
  materialId: null | number = null;
  constructor(
    private readonly loaderService: LoaderService,
    private readonly commonService: CommonService,
    private readonly drawerService: DrawerService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerParams();
  }

  private setupDrawerParams(): void {
    this.drawerService
      .getParams<ManageOrganizationDrawerParams>(DRAWER_IDS.MANAGE_ORG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }

  openAddReferenceMaterialDrawer(): void {
    this.drawerService
      .openDrawer(
        AddReferenceMaterialComponent,
        'Add Reference Material',
        520,
        true,
        undefined,
        {
          organizationId: this.orgParams.organizationId,
          mode: SettingMode.CREATE,
          organizationName: this.orgParams.organizationName,
        },
        DRAWER_IDS.REFERENCE_MATERIAL
      )
      .afterClose.subscribe(() => {
        this.loadReferenceMaterials();
      });
  }

  private loadReferenceMaterials(): void {
    this.loaderService.show();
    this.manageSettingSerivce
      .getReferenceMaterials({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        sortOrder: this.sortOrder,
        sort: this.sort,
        organizationId: this.orgParams.organizationId,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.result) {
            this.total = response.result.totalRows;
          }
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
        complete: () => {
          this.loaderService.hide();
        },
      });
  }

  fileExtension(name: string): string {
    return name.split('.').pop() || '';
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    this.pageSize = params.pageSize;
    this.pageIndex = params.pageIndex;
    const currentSort = params.sort.find(item => item.value !== null);
    this.sort = currentSort?.key || '';
    let sortOrder: number | undefined;
    if (currentSort?.value) {
      sortOrder = currentSort.value === 'ascend' ? 0 : 1;
    } else {
      sortOrder = undefined;
    }
    this.sortOrder = sortOrder;
    this.loadReferenceMaterials();
  }

  downloadFile(attachment: ReferenceMaterials): void {
    window.open(attachment.fileUrl, '_blank');
  }

  onDelete(id: number): void {
    this.showConfirmDialog = true;
    this.materialId = id;
  }
  handleDelete(): void {
    if (!this.materialId) return;

    this.isDeleting = true;
    this.manageSettingSerivce
      .deleteReferenceMaterial(this.materialId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isDeleting = false;
          this.showConfirmDialog = false;
        })
      )
      .subscribe({
        next: () => {
          this.commonService.notify('success', 'Activity deleted successfully');
          this.loadReferenceMaterials();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  handleCancel(): void {
    this.showConfirmDialog = false;
    this.materialId = 0;
  }

  ngOnDestroy(): void {
    this.orgParams.mode = OrgManagementMode.VIEW;
    this.destroy$.next();
    this.destroy$.complete();
  }
}
